import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export interface SalesUser {
  id: string;
  auth_user_id?: string;
  email?: string;
  first_name?: string;
  last_name?: string;
  display_name?: string;
  avatar_url?: string;
  active: boolean;
  system_role_id?: string;
  department_id?: string;
  system_role?: {
    id: string;
    name: string;
    code: string;
  };
}

export const useSalesUsers = () => {
  const { data: salesUsers, isLoading, error } = useQuery({
    queryKey: ['sales-users'],
    queryFn: async () => {
      // Get both sales_dev and sales_consultant roles
      const { data: salesRoles, error: roleError } = await supabase
        .from('system_roles')
        .select('id, code, name')
        .in('code', ['sales_dev', 'sales_consultant', 'sales_manager'])
        .eq('is_active', true);

      if (roleError) throw roleError;
      
      if (!salesRoles || salesRoles.length === 0) {
        console.warn('No sales roles found (sales_dev or sales)');
        return [];
      }

      const roleIds = salesRoles.map(role => role.id);

      // Get users with sales development or sales roles
      const { data: users, error: usersError } = await supabase
        .from('app_users')
        .select(`
          *,
          system_role:system_roles(id, name, code)
        `)
        .in('system_role_id', roleIds)
        .eq('active', true)
        .order('first_name');

      if (usersError) throw usersError;
      
      // Fetch user identity links for deduplication
      const { data: links, error: linksError } = await supabase
        .from('user_identity_links')
        .select('legacy_user_id, current_user_id');

      if (linksError) throw linksError;

      // Create a map of legacy_user_id -> current_user_id
      const legacyToCurrent = new Map<string, string>();
      (links || []).forEach(link => {
        legacyToCurrent.set(link.legacy_user_id, link.current_user_id);
      });

      // Filter out legacy users (keep only current users)
      const deduplicatedUsers = (users || []).filter(user => {
        return !legacyToCurrent.has(user.id);
      });

      // Fallback deduplication by email
      const emailToWinner = new Map<string, SalesUser>();
      
      deduplicatedUsers.forEach((user) => {
        if (!user.email) return;
        const normalizedEmail = user.email.trim().toLowerCase();
        
        if (!emailToWinner.has(normalizedEmail)) {
          emailToWinner.set(normalizedEmail, user);
        } else {
          const currentWinner = emailToWinner.get(normalizedEmail)!;
          let shouldReplace = false;
          
          const isCurrentInLinks = (links || []).some(l => l.current_user_id === user.id);
          const winnerIsCurrentInLinks = (links || []).some(l => l.current_user_id === currentWinner.id);
          
          if (isCurrentInLinks && !winnerIsCurrentInLinks) {
            shouldReplace = true;
          } else if (!isCurrentInLinks && !winnerIsCurrentInLinks) {
            if (user.auth_user_id && !currentWinner.auth_user_id) {
              shouldReplace = true;
            } else if (user.auth_user_id === currentWinner.auth_user_id) {
              const userNameScore = (user.display_name ? 3 : 0) + (user.first_name ? 1 : 0) + (user.last_name ? 1 : 0);
              const winnerNameScore = (currentWinner.display_name ? 3 : 0) + (currentWinner.first_name ? 1 : 0) + (currentWinner.last_name ? 1 : 0);
              
              if (userNameScore > winnerNameScore) {
                shouldReplace = true;
              }
            }
          }
          
          if (shouldReplace) {
            emailToWinner.set(normalizedEmail, user);
          }
        }
      });

      const finalUsers = Array.from(emailToWinner.values());
      
      return finalUsers as SalesUser[];
    },
  });

  return { 
    salesUsers: salesUsers || [], 
    isLoading, 
    error,
    count: salesUsers?.length || 0 
  };
};

